import { useMemo, useContext } from 'react';
import { gql, useMutation } from '@apollo/client';
import Dropzone, { useDropzone } from 'react-dropzone';
import { Box, Grid, Button, Typography } from '@mui/material';
import { baseStyle, activeStyle, acceptStyle, rejectStyle, libertyProjects } from '../../../../utils/Constants';
import { UnitContext } from '../../../../context/UnitContext';
import PdfCard from '../../../common/PdfCard';
import { accessAllowed } from '../../../../features/project/projectHooks';
import { useSelector } from 'react-redux';
import { selectProject } from '../../../../features/project/projectSlice';
import { selectUser } from '../../../../features/auth/authSlice';
import { useAppDispatch } from '../../../../app/hooks';
import { showSuccessSnackbar } from '../../../../features/snackbar/snackbarSlice';

const Miscellaneous = (props: ChildProps) => {
  const storeDispatch = useAppDispatch();
  const project = useSelector(selectProject);
  const user = useSelector(selectUser);
  const { miscellaneous, setMiscellaneous, createMiscellaneous, deleteSupport, handleDrop } = props;
  const { filteredDeal, setFilteredDeal } = useContext(UnitContext);
  const { isDragActive, isDragAccept, isDragReject } = useDropzone({
    accept: 'image/jpeg, image/png, application/pdf',
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  const [moveDocuments] = useMutation(MOVEDOCUMENTS, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar(`Moved to Deposit Images!`));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const handleDelete = (numIndex: number, id: string) => {
    if (filteredDeal.miscellaneous.length && filteredDeal.miscellaneous[numIndex]._id) {
      deleteSupport({
        variables: { _id: filteredDeal._id, documentId: filteredDeal.miscellaneous[numIndex]._id, deleteFile: true, type: 'miscellaneous' },
      });
    }
    let filterMiscellaneous = miscellaneous.filter((miscellaneous: IMiscellaneous, index: number) => {
      return index !== numIndex;
    });
    setMiscellaneous(filterMiscellaneous);
  };

  const saveMiscellaneous = async () => {
    if (miscellaneous.length > 0) {
      await miscellaneous.forEach((file: any, index: number) => {
        if (file.file) {
          createMiscellaneous({
            variables: { id: filteredDeal._id, name: `misc${index + 1}-${file.file.name}`, file: file.file, type: 'miscellaneous' },
          });
        }
      });
    }
  };

  const handleMoveImage = async (numIndex: number, id: string) => {
    moveDocuments({
      variables: {
        _id: filteredDeal._id,
        current: 'miscellaneous',
        destination: 'cheques',
        name: id
      },
    }).then((res) => {
      let filterDepositImages = filteredDeal.miscellaneous.filter((depositImages: any, index: number) => {
        return index !== numIndex;
      });
      setFilteredDeal({
        ...filteredDeal,
        miscellaneous: filterDepositImages,
        depositImages: res.data.moveDocuments.depositImages,
      });
      setMiscellaneous(filterDepositImages)
    });
  };

  return (
    <div>
      <Typography variant="h2" sx={{ mb: 1 }}>
        <strong>Miscellaneous</strong>
      </Typography>
      <Box sx={{ mb: libertyProjects.includes(project._id) ? 1 : 3 }}>
        Upload any documents that do not need to be sent out for signing here (Ex. Third Party Verification)
      </Box>
      {/* Liberty projects: the Outstandings "Liberty Additional Documents" column checks these names */}
      {libertyProjects.includes(project._id) ? (
        <Box sx={{ mb: 3, fontSize: '0.85rem' }}>
          <Box>
            <strong>Email Confirmation:</strong> upload the purchaser's email confirmation with <strong>"Email"</strong> in the file name
            (Ex. Email Confirmation.pdf).
          </Box>
          <Box>
            <strong>Lawyer Information:</strong> upload the lawyer's information with <strong>"Lawyer"</strong> in the file name (Ex.
            Lawyer Information.pdf).
          </Box>
          <Box>Until both are uploaded, they will show as outstanding in the Outstandings page.</Box>
        </Box>
      ) : null}
      <Box sx={{ mb: 2 }}>
        <Dropzone onDrop={(files) => handleDrop(files, 'miscellaneous')} accept="image/jpg, image/jpeg, image/png, application/pdf">
          {({ getRootProps, getInputProps }) => (
            <section>
              <div {...getRootProps({ style })}>
                <input {...getInputProps()} />
                <p>Drag and Drop or Upload Miscellaneous Documents</p>
              </div>
            </section>
          )}
        </Dropzone>
      </Box>
      <div>
        <Grid container spacing={2}>
          {miscellaneous.length > 0
            ? miscellaneous.map((attachment: any, index: number) => {
                return (
                  <Grid key={index} item lg={3} md={4} sm={6} xs={12}>
                    <PdfCard
                      file={attachment.getUrl ? attachment.getUrl : attachment.url}
                      title={attachment.name ? attachment.name : attachment.file.name}
                      id={index}
                      handleDelete={handleDelete}
                      handleMoveImage={handleMoveImage}
                      download={true}
                      index={index}
                      type={'miscellaneous'}
                    />
                  </Grid>
                );
              })
            : null}
        </Grid>
        {accessAllowed(user, project._id, 'editDeals') ? (
          <Button sx={{ mt: 1 }} onClick={() => saveMiscellaneous()} name={'update'} color="success" variant="contained">
            Save Miscellaneous Documents
          </Button>
        ) : null}
      </div>
    </div>
  );
};

interface IMiscellaneous {
  url: string;
  file: any;
  name: string;
  putUrl: string;
  getUrl: string;
  size?: number;
}

interface ChildProps {
  miscellaneous: any;
  setMiscellaneous: any;
  createMiscellaneous: any;
  deleteSupport: any;
  handleDrop: any;
}

const MOVEDOCUMENTS = gql`
  mutation moveDocuments($_id: MongoID!, $documentId: MongoID, $current: String!, $destination: String!, $name: String) {
    moveDocuments(_id: $_id, documentId: $documentId, current: $current, destination: $destination, name: $name) {
      depositImages {
        getUrl
        name
      }
      miscellaneous {
        _id
        name
        getUrl
      }
    }
  }
`;

export default Miscellaneous;
